/*
__/\\\\\\\\\\\\\\\__/\\\\\\\\\\\\\\\_____/\\\\\\\\\____        
 _\///////\\\/////__\///////\\\/////____/\\\\\\\\\\\\\__       
  _______\/\\\_____________\/\\\________/\\\/////////\\\_      
   _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_     
    _______\/\\\_____________\/\\\_______\/\\\\\\\\\\\\\\\_    
     _______\/\\\_____________\/\\\_______\/\\\/////////\\\_   
      _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_  
       _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_ 
        _______\///______________\///________\///________\///__
            
            COPYRIGHT TACTICAL TRANSPORTATION ADVISORS, INC. 
            ALL RIGHTS RESERVED.
*/

import { useEffect, useState } from "react";
import { Badge, Button, Dropdown, Form, ListGroup, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faHistory, faMagnifyingGlass, faPaperclip, faShield, faThumbsDown, faTrashAlt, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import QuickTable from "../../../components/QuickTable";
import CustomButton from "../../../components/CustomButton";
import KeyValueRow from "../../../components/KeyValueRow";
import AlertModal from "../../../components/AlertModals/AlertModal";
import EDDocumentPreview from "../EDDocumentPreview";
import TextAreaControl from "../../../components/TextAreaControl";
import { AppGlobals } from "../../../App";
import { downloadBase64, formatName, OnboardingDocumentsReadOnly } from "../../../tools";
import {
  deleteDocumentRequestDocument,
  downloadDocumentRequestDocument,
  downloadDocumentRequestDocumentAttachment,
  previewRejectedDocument,
  rejectDocumentRequestDocument,
  sendDocumentRequestInvitationOnboarding,
} from "../../../services/HRDocumentsService";

export default function DocumentRequestDetailsOnboarding({ documentRequest, handleRemoveDocumentRequestDocument, handleSetDocumentRequestDocumentIncomplete }) {
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const [revisionMessage, setRevisionMessage] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  async function handleSendInvitation(sendTo) {
    setIsSendingInvitation(true);
    const response = await sendDocumentRequestInvitationOnboarding(documentRequest.uid, sendTo);
    if (response.status == 200) {
      AppGlobals.alert("success", "Sent Document Request Invitation");
    } else {
      AppGlobals.alert("danger", response.message);
    }
    setIsSendingInvitation(false);
  }

  async function handleRejectDocument() {
    setIsRejecting(true);
    const response = await rejectDocumentRequestDocument(documentRequest.uid, selectedDocument.documentType, revisionMessage);
    if (response.status == 200) {
      handleSetDocumentRequestDocumentIncomplete(documentRequest.uid, selectedDocument.documentType);
      setRevisionMessage("");
      setShowRejectModal(false);
    }
    setIsRejecting(false);
  }

  async function handleDownloadDocument(document, setIsDownloading) {
    setIsDownloading(true);
    if (document.base64) {
      downloadBase64(document.base64, `${documentRequest.employee?.firstName} ${documentRequest.employee?.lastName} ${OnboardingDocumentsReadOnly[document.documentType]}`);
    } else {
      const response = await downloadDocumentRequestDocument(document.uid, document.documentType);
      if (response.status == 200) {
        document.base64 = response.base64;
        downloadBase64(response.base64, `${documentRequest.employee?.firstName} ${documentRequest.employee?.lastName} ${OnboardingDocumentsReadOnly[document.documentType]}`);
      }
    }
    setIsDownloading(false);
  }

  async function handleDownloadDocumentAttachment(document, setIsDownloading, attachmentNum) {
    setIsDownloading(true);
    const response = await downloadDocumentRequestDocumentAttachment(document.uid, document.documentType, attachmentNum);
    if (response.status == 200) {
      downloadBase64(response.base64, `${documentRequest.employee?.firstName} ${documentRequest.employee?.lastName} ${OnboardingDocumentsReadOnly[document.documentType]} attachment ${attachmentNum}`);
    }
    setIsDownloading(false);
  }

  async function handlePreviewDocument(document, setIsPreviewing) {
    setIsPreviewing(true);
    if (document.base64) {
      handlePreviewPdfOrOldAttachment(document);
    } else {
      const response = await downloadDocumentRequestDocument(document.uid, document.documentType);
      if (response.status == 200) {
        document.base64 = response.base64;
        handlePreviewPdfOrOldAttachment(document);
      }
    }
    setIsPreviewing(false);
  }

  function handlePreviewPdfOrOldAttachment(document) {
    setSelectedDocument(document);
    setShowPreviewModal(true);
  }

  function handleShowRejectModal(document) {
    setSelectedDocument(document);
    setShowRejectModal(true);
  }

  const sortedDocuments = Object.entries(documentRequest.documents).filter(([key]) => OnboardingDocumentsReadOnly[key] !== undefined);

  const documentRequestDocumentElements = sortedDocuments.map(([_, document]) => (
    <DocumentRequestDocumentElement
      key={document.documentType}
      document={document}
      documentRequest={documentRequest}
      handlePreviewDocument={handlePreviewDocument}
      handleDownloadDocument={handleDownloadDocument}
      handleDownloadDocumentAttachment={handleDownloadDocumentAttachment}
      handleShowRejectModal={handleShowRejectModal}
      handleRemoveDocumentRequestDocument={handleRemoveDocumentRequestDocument}
    />
  ));

  const activityElements = documentRequest.activity.map((item) => (
    <KeyValueRow isListItem key={item.uid} title={item.isEmployer ? "Employer" : "Employee"} value={moment(item.dateTime).format("MMM D, YYYY [at] h:mm A")} />
  ));

  let employerName;
  if (documentRequest.employerSigner) {
    const mid = documentRequest.employerSigner.middleName ? ` ${documentRequest.employerSigner.middleName}` : "";
    employerName = `${documentRequest.employerSigner.firstName}${mid} ${documentRequest.employerSigner.lastName}`;
  }

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Document Request</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
          {documentRequest.employerSigner ? (
            <h5><b>Employer Signer: </b>{employerName}</h5>
          ) : (
            <div />
          )}
          <Dropdown style={{ alignSelf: "flex-end", marginBottom: 12 }}>
            <Dropdown.Toggle disabled={isSendingInvitation || documentRequest.migrated} title={documentRequest.migrated ? "This document request has been migrated to a new system." : undefined}>
              {isSendingInvitation ? <Spinner size="sm" /> : <span>Resend Invitation</span>}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleSendInvitation("employee")}>Send To Employee</Dropdown.Item>
              <Dropdown.Item onClick={() => handleSendInvitation("employer")}>Send To Employer</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {documentRequestDocumentElements.length > 0 && (
          <QuickTable
            title="Documents"
            headers={["Document Type", "Employee Completed", "Employer Completed", "Actions"]}
            rows={documentRequestDocumentElements}
            widths={[null, null, null, 1]}
            size="sm"
          />
        )}

        <h5>Request View Activity</h5>
        <ListGroup style={{ maxHeight: 400, overflow: "auto" }}>
          {activityElements.length > 0 ? activityElements : <ListGroup.Item>None</ListGroup.Item>}
        </ListGroup>
      </Modal.Body>

      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send Document Back to Employee to Revise?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <span>Include a message below for the employee with instructions on what needs revised.</span>
          <Form.Control value={revisionMessage} maxLength={500} onChange={(e) => setRevisionMessage(e.target.value)} as="textarea" rows={5} />
        </Modal.Body>
        <Modal.Footer>
          <CustomButton label="Request Revision" disabled={!revisionMessage} isLoading={isRejecting} onClick={handleRejectDocument} />
        </Modal.Footer>
      </Modal>

      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} fullscreen>
        <EDDocumentPreview document={selectedDocument} />
      </Modal>
    </>
  );
}

function DocumentRequestDocumentElement({ document, documentRequest, handlePreviewDocument, handleDownloadDocument, handleDownloadDocumentAttachment, handleShowRejectModal, handleRemoveDocumentRequestDocument }) {
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingDDAttachment1, setIsDownloadingDDAttachment1] = useState(false);
  const [isDownloadingDDAttachment2, setIsDownloadingDDAttachment2] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRejectedVersionsModal, setShowRejectedVersionsModal] = useState(false);
  const [selectedRejectedDocuments, setSelectedRejectedDocuments] = useState([]);

  async function handleDeleteDocumentRequestDocument() {
    const response = await deleteDocumentRequestDocument(document.uid, document.documentType);
    if (response.status == 200) {
      handleRemoveDocumentRequestDocument(document.documentRequestIdentifier, document.documentType);
    }
  }

  const employerCompletedCellContent =
    document?.employerCompleted !== undefined
      ? document.employerCompleted ? moment(document.employerCompleted).format("MMM D, YYYY") : "Awaiting Completion"
      : "";

  return (
    <>
      <tr>
        <td>
          <div className="d-flex align-items-center gap-2">
            {OnboardingDocumentsReadOnly[document.documentType]}
            {["dd", "mdd"].includes(document.documentType) && Boolean(document.isPlaid) && Boolean(document.employeeCompleted) && (
              <OverlayTrigger placement="top" overlay={<Tooltip>This user has successfully verified their bank account information using Plaid.</Tooltip>}>
                <Badge bg="secondary" style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: "0.75rem" }}>
                  <FontAwesomeIcon icon={faShield} /> Plaid
                </Badge>
              </OverlayTrigger>
            )}
          </div>
        </td>
        <td style={{ color: !document.employeeCompleted ? "red" : undefined }}>
          {document.employeeCompleted ? moment(document.employeeCompleted).format("MMM D, YYYY") : "Awaiting Completion"}
        </td>
        <td style={{ color: "employerCompleted" in document && !document.employerCompleted ? "red" : undefined }}>
          {employerCompletedCellContent}
        </td>
        <td>
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            {document.employeeCompleted ? (
              <>
                <CustomButton title="Preview" style={{ padding: "0px 6px" }} label={<FontAwesomeIcon icon={faMagnifyingGlass} />} isLoading={isPreviewing} onClick={() => handlePreviewDocument(document, setIsPreviewing)} />
                <CustomButton title="Download" style={{ padding: "0px 6px" }} label={<FontAwesomeIcon icon={faDownload} />} isLoading={isDownloading} onClick={() => handleDownloadDocument(document, setIsDownloading)} />
                {["dd", "mdd"].includes(document.documentType) && (
                  <>
                    {Boolean(document.attachment1DataIdentifier) && (
                      <CustomButton title="Download Attachment 1" style={{ padding: "0px 6px" }} label={<div style={{ display: "flex", gap: 2 }}><FontAwesomeIcon icon={faPaperclip} /><FontAwesomeIcon icon={faDownload} /></div>} isLoading={isDownloadingDDAttachment1} onClick={() => handleDownloadDocumentAttachment(document, setIsDownloadingDDAttachment1, 1)} />
                    )}
                    {Boolean(document.attachment2DataIdentifier) && (
                      <CustomButton title="Download Attachment 2" style={{ padding: "0px 6px" }} label={<div style={{ display: "flex", gap: 2 }}><FontAwesomeIcon icon={faPaperclip} /><FontAwesomeIcon icon={faDownload} /></div>} isLoading={isDownloadingDDAttachment2} onClick={() => handleDownloadDocumentAttachment(document, setIsDownloadingDDAttachment2, 2)} />
                    )}
                  </>
                )}
                <CustomButton title="Rejection History" style={{ padding: "0px 6px" }} disabled={!documentRequest.rejectedDocuments[document.documentType]} label={<FontAwesomeIcon icon={faHistory} />} onClick={() => { setSelectedRejectedDocuments(documentRequest.rejectedDocuments[document.documentType]); setShowRejectedVersionsModal(true); }} />
                <Button variant="danger" title={documentRequest.migrated ? "This document request has been migrated and cannot be modified." : "Request Revision"} style={{ padding: "0px 6px" }} disabled={documentRequest.migrated} onClick={() => handleShowRejectModal(document)}>
                  <FontAwesomeIcon icon={faThumbsDown} />
                </Button>
              </>
            ) : (
              documentRequest?.rejectedDocuments?.[document.documentType]?.length > 0 && (
                <CustomButton title="Rejection History" style={{ padding: "0px 6px" }} label={<FontAwesomeIcon icon={faHistory} />} onClick={() => { setSelectedRejectedDocuments(documentRequest.rejectedDocuments[document.documentType]); setShowRejectedVersionsModal(true); }} />
              )
            )}
            <Button title={documentRequest.migrated ? "This document request has been migrated and cannot be modified." : "Delete"} variant="danger" style={{ padding: "0px 6px" }} disabled={documentRequest.migrated} onClick={() => setShowDeleteModal(true)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </Button>
          </div>
        </td>
      </tr>
      <AlertModal centered show={showDeleteModal} onHide={() => setShowDeleteModal(false)} title="Permanently Delete Document" message="This cannot be undone." buttonLabel="Delete Document" callBack={handleDeleteDocumentRequestDocument} />
      <Modal centered show={showRejectedVersionsModal} onHide={() => setShowRejectedVersionsModal(false)}>
        <RejectedDocumentsModal rejectedDocuments={selectedRejectedDocuments} />
      </Modal>
    </>
  );
}

function RejectedDocumentsModal({ rejectedDocuments }) {
  const documentType = rejectedDocuments[0].documentType;
  const [document, setDocument] = useState();
  const [isPreviewing, setIsPreviewing] = useState();

  async function previewDocument(rejectedDocument) {
    setIsPreviewing(rejectedDocument.uid);
    const response = await previewRejectedDocument(documentType, rejectedDocument.uid);
    if (response.status == 200) {
      response.documentRequestDocument.documentRejection = rejectedDocument.documentRejection;
      response.documentRequestDocument.documentType = documentType;
      if (response.documentRequestDocument.employerSubmission) {
        response.documentRequestDocument.base64 = response.documentRequestDocument.employerSubmission;
      } else if (response.documentRequestDocument.employeeSubmission) {
        response.documentRequestDocument.base64 = response.documentRequestDocument.employeeSubmission;
      }
      setDocument(response.documentRequestDocument);
    } else {
      AppGlobals.alert("danger", response.message);
    }
    setIsPreviewing(undefined);
  }

  const rows = rejectedDocuments.map((rejectedDocument) => (
    <tr key={rejectedDocument.uid}>
      <td>{moment(rejectedDocument.documentRejection.author.dateTime).format("lll")}</td>
      <td>{formatName(rejectedDocument.documentRejection.author.user, 2)}</td>
      <td>
        <CustomButton title="Preview" style={{ padding: "0px 6px" }} label={<FontAwesomeIcon icon={faMagnifyingGlass} />} isLoading={isPreviewing === rejectedDocument.uid} onClick={() => previewDocument(rejectedDocument)} />
      </td>
    </tr>
  ));

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Rejected Versions for {OnboardingDocumentsReadOnly[documentType]}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <QuickTable headers={["Date Rejected", "Rejected By", "View"]} rows={rows} widths={[null, null, null]} size="sm" />
      </Modal.Body>
      <Modal fullscreen show={Boolean(document)} onHide={() => setDocument(undefined)}>
        {Boolean(document) && <RejectedDocumentPreview rejectedDocument={document} />}
      </Modal>
    </>
  );
}

function RejectedDocumentPreview({ rejectedDocument }) {
  const [base64] = useState(rejectedDocument.base64);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (base64) {
      fetch(base64)
        .then((r) => r.blob())
        .then((blob) => setUrl(window.URL.createObjectURL(blob)))
        .catch(console.error);
    }
  }, [base64]);

  const renderDocument = () => {
    if (base64?.split(",", 1)[0].includes("pdf")) {
      return (
        <div style={{ backgroundColor: "#f4f4f5", height: "100%", width: "100%", overflow: "hidden" }}>
          <iframe src={url} width="100%" height="100%" title="Embedded Page" style={{ border: "none", overflow: "hidden" }} />
        </div>
      );
    } else if (base64) {
      return (
        <div style={{ display: "flex", width: "100%", height: "100%", alignItems: "center", justifyContent: "center", borderRadius: 4, backgroundColor: "#f4f4f5", overflow: "hidden" }}>
          <img src={base64} alt="document" style={{ maxWidth: "100%", maxHeight: "100%" }} />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Previewing Rejected Document</Modal.Title>
          <p style={{ margin: 0, fontSize: "0.9rem", opacity: 0.7 }}>
            {`${OnboardingDocumentsReadOnly[rejectedDocument.documentType]} — Rejected by ${formatName(rejectedDocument.documentRejection.author.user, 2)} — ${moment(rejectedDocument.documentRejection.author.dateTime).format("lll")}`}
          </p>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div style={{ marginBottom: 8 }}>
          <TextAreaControl rows={5} label="Revision Message" value={rejectedDocument.documentRejection.rejectionMessage} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", height: "100%" }}>
          {renderDocument() ?? (
            <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", borderRadius: 8, padding: 16, color: "var(--bs-danger)" }}>
              <FontAwesomeIcon icon={faXmarkCircle} style={{ marginRight: 8, height: 24, width: 24 }} />
              <span style={{ fontWeight: 500 }}>Failed to preview document</span>
            </div>
          )}
        </div>
      </Modal.Body>
    </>
  );
}
